import { makeStyles, tokens, Text } from '@fluentui/react-components';
import { MOBILE_BREAKPOINT } from './constants';
import { STACK_PILLS } from './data';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  section: {
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
      gap: '40px',
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  heading: {
    display: 'block',
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalL,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    marginBlockEnd: tokens.spacingVerticalXL,
  },
  subHeading: {
    display: 'block',
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalXS,
    marginBlockStart: tokens.spacingVerticalL,
  },
  subBody: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground2,
  },
  mockCard: {
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  layer: {
    paddingBlock: '18px',
    paddingInline: '20px',
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  layerTop: {
    backgroundImage: ottoTokens.landingScenarioBackground,
    border: `1px solid ${ottoTokens.landingAccentBorder}`,
  },
  layerUnder: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  layerLabel: {
    display: 'block',
    fontSize: tokens.fontSizeBase100,
    color: ottoTokens.landingDimText,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBlockEnd: tokens.spacingVerticalXS,
  },
  layerTitle: {
    display: 'block',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalXS,
  },
  layerDesc: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1.5,
    color: tokens.colorNeutralForeground2,
  },
  divider: {
    textAlign: 'center',
    color: ottoTokens.landingDimText,
    fontSize: tokens.fontSizeBase100,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    paddingBlock: tokens.spacingVerticalXS,
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginBlockStart: tokens.spacingVerticalM,
  },
  pill: {
    fontSize: tokens.fontSizeBase100,
    paddingBlock: '4px',
    paddingInline: '10px',
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusCircular,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
  },
});

export const ManagedEnterpriseSection = () => {
  const styles = useStyles();

  return (
    <section className={styles.section} aria-labelledby="managed-enterprise-title">
      <div className={styles.inner}>
        <div>
          <Text className={styles.eyebrow}>Managed, yet enterprise</Text>
          <Text as="h2" id="managed-enterprise-title" className={styles.heading}>
            All the power of Azure — no setup.
          </Text>
          <Text className={styles.lead}>
            Cloud service simplicity with Azure-grade isolation, networking, and security — just
            sign in and build.
          </Text>
          <Text as="h3" className={styles.subHeading}>Isolated compute, on by default</Text>
          <Text className={styles.subBody}>
            Every project gets an isolated compute boundary. Your workflows, data, and agents
            never share a runtime with others — no infrastructure changes needed.
          </Text>
          <Text as="h3" className={styles.subHeading}>Virtual network support built in</Text>
          <Text className={styles.subBody}>
            Securely reach private databases, internal APIs, and on-premises systems through VNET
            integration and private endpoints — without exposing them to the internet.
          </Text>
          <Text as="h3" className={styles.subHeading}>Governance from day one</Text>
          <Text className={styles.subBody}>
            Identity, role-based access control, audit logging, and policy controls are on by
            default. Admins set the guardrails. Builders work inside them.
          </Text>
        </div>
        <div className={styles.mockCard} aria-hidden="true">
          <div className={`${styles.layer} ${styles.layerTop}`}>
            <Text className={styles.layerLabel}>What you get</Text>
            <Text className={styles.layerTitle}>A clean web experience</Text>
            <Text className={styles.layerDesc}>
              Sign in. Build workflows. Ship. No Azure resource groups or resources to manage.
            </Text>
          </div>
          <Text className={styles.divider}>Powered by</Text>
          <div className={`${styles.layer} ${styles.layerUnder}`}>
            <Text className={styles.layerLabel}>What runs underneath</Text>
            <Text className={styles.layerTitle}>Azure-grade infrastructure, managed for you</Text>
            <div className={styles.pills}>
              {STACK_PILLS.map((pill) => (
                <span key={pill} className={styles.pill}>{pill}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
